import { IconButton } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";
import { DatasetPreview } from "@/components/dataset/DatasetPreview";
import { DatasetPreviewPanel } from "@/components/dataset/selection/DatasetPreviewPanel";
import { createThumbnailRefreshDebouncer } from "@/components/dataset/thumbnailRefresh";
import { FilterSearchInput } from "@/components/filter/FilterSearchInput";
import { HatListView } from "@/components/table/HatListView";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { formatNumber } from "@/utils/formats";
import type { MultiDatasetViewController } from "./useMultiDatasetViewController";
import styles from "./MultiDatasetView.module.css";

// 줄을 고른 뒤 상세를 바꾸기까지의 지연. 화살표로 목록을 훑는 동안 줄마다 영상 연결을 열지 않는다.
// 제출 위저드의 미리보기 지연과 같은 값이다.
const DETAIL_DELAY_MS = 200;

export type MultiDatasetViewDataset = {
  uuid: string;
  displayName: string;
  thumbnailUrl: string | null;
};

// 목록 항목 하나를 base 가 그릴 수 있는 모양으로 바꾼 값.
export type MultiDatasetViewItem = {
  // 원본 Dataset. 삭제됐거나 registry 밖의 데이터셋이면 null 이다. 그 항목은 상세를 열 수 없다.
  dataset: MultiDatasetViewDataset | null;
  // 원본이 없을 때 줄에 적는 이름.
  fallbackLabel: string;
};

export type MultiDatasetViewProps<T> = {
  controller: MultiDatasetViewController<T>;
  // 목록의 접근성 이름. 예) "Session 입력 Dataset"
  ariaLabel: string;
  describeItem: (item: T) => MultiDatasetViewItem;
  // 줄 안, 이름 아래에 두는 보조 정보. 줄 전체가 선택 버튼이라 여기에 버튼·링크를 넣을 수 없다.
  renderItemMeta?: (item: T) => ReactNode;
  // 줄 오른쪽에 두는 조작 요소. 줄 선택과 따로 동작한다.
  renderItemActions?: (item: T) => ReactNode;
};

// Dataset 여러 개를 한 화면에서 본다. 왼쪽은 검색과 목록, 오른쪽은 고른 Dataset 의 상세다.
//
// 이 컴포넌트는 목록이 어디서 오는지 모른다. 항목의 타입과 조회는 소비처가 정하고(controller 의
// `loadPage`), 줄에 얹는 정보도 소비처가 주입한다(`renderItemMeta`·`renderItemActions`). 학습 세션의
// 입력, 평가의 대상처럼 소비처마다 줄에 보여줄 값이 다르기 때문이다.
//
// 높이는 부모가 준다(`height: 100%`). 목록과 상세가 각자 스크롤한다.
export function MultiDatasetView<T>({
  controller,
  ariaLabel,
  describeItem,
  renderItemMeta,
  renderItemActions,
}: MultiDatasetViewProps<T>): ReactNode {
  const { reload } = controller;
  // 섬네일 서명 URL 이 만료되면 목록을 다시 받는다. 여러 줄이 함께 만료되어도 한 번만 받는다.
  const refreshThumbnails = useMemo(() => createThumbnailRefreshDebouncer(reload), [reload]);

  const selectedItem =
    controller.selectedKey === null
      ? null
      : (controller.items.find((item) => controller.getKey(item) === controller.selectedKey) ??
        null);
  const selected = selectedItem === null ? null : describeItem(selectedItem);

  return (
    <div className={styles.root}>
      <div className={styles.listCol}>
        <FilterSearchInput
          value={controller.search}
          onCommit={controller.setSearch}
          ariaLabel="Dataset 검색"
          placeholder="Dataset 이름을 검색해 보세요."
        />
        {controller.status === "success" ? (
          <p className={styles.count} role="status">
            {controller.search === "" ? "전체" : "검색 결과"} {formatNumber(controller.total)}건
          </p>
        ) : null}
        <HatListView
          className={styles.list}
          ariaLabel={ariaLabel}
          items={controller.items}
          getKey={controller.getKey}
          selectedKey={controller.selectedKey}
          onSelect={(item) => controller.select(controller.getKey(item))}
          status={controller.status}
          errorMessage={controller.errorMessage}
          onRetry={controller.reload}
          emptyMessage={
            controller.search === ""
              ? "Dataset이 없습니다."
              : "일치하는 Dataset이 없습니다. 다른 검색어를 입력해 보세요."
          }
          hasMore={controller.hasMore}
          loadingMore={controller.loadingMore}
          onLoadMore={controller.loadMore}
          renderItem={(item) => {
            const described = describeItem(item);
            const meta = renderItemMeta?.(item);
            return {
              content: (
                <div className={styles.row}>
                  <DatasetPreview
                    label={described.dataset?.displayName ?? described.fallbackLabel}
                    imageUrl={described.dataset?.thumbnailUrl ?? null}
                    mode="table"
                    onImageError={refreshThumbnails}
                  />
                  <div className={styles.rowText}>
                    <span
                      className={styles.rowName}
                      data-missing={described.dataset === null ? "true" : undefined}
                    >
                      {described.dataset?.displayName ?? described.fallbackLabel}
                    </span>
                    {meta ? <div className={styles.rowMeta}>{meta}</div> : null}
                  </div>
                </div>
              ),
              actions: renderItemActions?.(item),
            };
          }}
        />
      </div>
      <div className={styles.detailCol}>
        <DatasetDetail selected={selected} status={controller.status} />
      </div>
    </div>
  );
}

type DatasetDetailProps = {
  selected: MultiDatasetViewItem | null;
  status: MultiDatasetViewController<unknown>["status"];
};

function DatasetDetail({ selected, status }: DatasetDetailProps): ReactNode {
  const dataset = selected?.dataset ?? null;
  const shownUuid = useDelayedValue(dataset?.uuid ?? null);

  if (selected === null) {
    // 목록을 받는 중이거나 실패했을 때는 목록 쪽이 상태를 알린다. 여기서는 비워 둔다.
    return status === "success" ? (
      <div className={styles.detailEmpty}>
        <EmptyState>목록에서 Dataset을 고르면 상세가 표시됩니다.</EmptyState>
      </div>
    ) : null;
  }
  if (dataset === null) {
    return (
      <div className={styles.detailEmpty}>
        <EmptyState>
          원본 Dataset이 없어 상세를 표시할 수 없습니다. ({selected.fallbackLabel})
        </EmptyState>
      </div>
    );
  }
  // 고른 항목과 다른 Dataset 을 잠깐이라도 보여주지 않는다.
  if (shownUuid !== dataset.uuid) {
    return null;
  }
  return (
    <div className={styles.detail}>
      <div className={styles.detailHeader}>
        <IconButton
          text="상세 창 열기"
          variant="surface"
          size="sm"
          onClick={() =>
            openDatasetDetailWindow({
              datasetUuid: dataset.uuid,
              displayName: dataset.displayName,
            })
          }
        />
      </div>
      {/* Dataset 이 바뀌면 패널을 새로 만든다. 에피소드 선택과 영상 상태가 앞 Dataset 에서 넘어오지 않는다. */}
      <DatasetPreviewPanel
        key={dataset.uuid}
        selection={{ uuid: dataset.uuid, label: dataset.displayName }}
      />
    </div>
  );
}

// 값을 잠시 뒤에 반영한다. 그 사이에 값이 또 바뀌면 앞 값은 버린다.
function useDelayedValue(value: string | null): string | null {
  const [shown, setShown] = useState<string | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setShown(value), DETAIL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [value]);
  return shown;
}
